import { Button } from "components/DesignSystem/Button/Button";
import { Paperclip } from "components/icons/Chat/Paperclip";
import { PaperPlaneRight } from "components/icons/Chat/PaperPlaneRight";
import { Smiley } from "components/icons/Chat/Smiley";
import { AISparkle } from "components/icons/AISparkle";
import { useCurrentChatGroupContext } from "hooks/useCurrentChatGroupContext";
import { useCallback, useEffect, useId, useRef } from "react";
import { UploadButton } from "react-file-utils";
import { motion } from "framer-motion";
import {
  AttachmentPreviewList,
  ChatAutoCompleteProps,
  MessageInputProps,
  StreamMessage,
  useChannelActionContext,
  useChannelStateContext,
  useChatContext,
  useMessageInputContext,
} from "stream-chat-react";
import {
  CustomTrigger,
  DefaultStreamChatGenerics,
} from "stream-chat-react/dist/types/types";

import { default as classNames, default as cx } from "classnames";
import ChatTemplate from "components/chat/ChatTemplate";
import { CartAttachmentPreviews } from "components/design/CartAttachmentPreview";
import { Cross } from "components/icons/Cross";
import { LoadingIcon } from "components/icons/LoadingIcon";
import { MessageBody } from "components/MessageBubble/MessageBubble";
import { MessageInputInfo } from "components/MessageInputInfo/MessageInputInfo";
import { ATTACH_CART } from "constants/analyticsEvents";
import { RICH_TEXT_MODE_STORAGE_KEY } from "constants/chatComposer";
import {
  AI_CHANNEL,
  CHAT_CARD_TYPE,
  FINANCIAL_CLOSING_CARD_TYPE,
  TASK_REVIEW_AND_PAY,
  TASK_REVIEW_DOCUMENT,
  TRANSACTIONS,
} from "constants/chatType";
import { useAnalytics } from "hooks/useAnalytics";
import { useAppSelector } from "hooks/useAppSelector";
import { useGetMessage } from "hooks/useGetMessage";
import { useIsSmallScreen } from "hooks/useIsSmallScreen";
import { useLockViewportScale } from "hooks/useLockViewportScale";
import { useIsGroupV2Platform } from "hooks/useIsGroupV2Platform";
import { useLocalStorage } from "hooks/useLocalStorage";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useToast } from "hooks/useToast";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import {
  useCreateMessageDraftMutation,
  useGenerateResponseForMessageMutation,
  useGetDraftMessageQuery,
  useGetLatestAIGeneratedMessageQuery,
  useStrengthenMessageMutation,
  useUpdateAIGeneratedMessageMutation,
} from "store/apis/chat";
import {
  useGetFinancialClosingQuery,
  useSendClosingToChatMutation,
  useUpdateFinancialClosingStatusMutation,
} from "store/apis/financialClosing";
import { useSendCartToChatMutation } from "store/apis/products";
import {
  useGetTaskFromChannelIdQuery,
  useSendDocumentToReviewMutation,
} from "store/apis/task";
import { usePushToChatMutation } from "store/apis/transactions";
import { useSendCardToStreamMutation } from "store/apis/estimatedTax";
import { setCartInChat } from "store/slices/cartInChat";
import { setDropedFile, setToOpenChatId } from "store/slices/chat";
import { clearEstimatedTaxCardInChat } from "store/slices/estimatedTaxCardInChat";
import { resetChatCardAttachment } from "store/slices/financialClosing";
import { setScrollToMessageId } from "store/slices/messageToScrollTo";
import {
  clearCustomAttachment,
  clearCustomLinks,
  setActiveChannelGroupId,
} from "store/slices/reviewAndBalancePayment";
import { setTransactionsInChat } from "store/slices/transactionsInChat";
import { RootState } from "store/store";
import { EmojiPicker } from "stream-chat-react/emojis";
import { BackendError } from "types/utils/error";
import { debounce } from "utils/debouncing";
import { AdminCall } from "./AdminCall/AdminCall";
import { ChannelFrozenBlocker } from "./ChannelFrozenBlocker";
import { CustomAttachmentPreviewList } from "./CustomAttachmentPreviewList";
import { FinancialClosingCardAttachment } from "./FinancialClosing/FinancialClosingCardAttachment";
import { TaskActionsFromChat } from "./TaskActionsFromChat";
import { TransactionsInChatCard } from "./TransactionsInChatCard/TransactionsInChatCard";
import { EstimatedTaxCardInChat } from "./EstimatedTaxCardInChat/EstimatedTaxCardInChat";
import { removeMessageToQuote } from "store/slices/messegeIdToRemind";
import { Sparkle } from "components/icons/Sparkle";
import {
  resetGeneratingMessage,
  setGeneratingMessage,
} from "store/slices/generateChatMessage";
import ToolTip from "components/design/toolTip";
import { FilingDraftForReview } from "./FilingDraftForReview";
import {
  useCreateDftDraftMutation,
  useCreateFilingDraftMutation,
} from "store/apis/filingDraftReview";
import { InkleAISuggestions } from "components/InkleAI/InkleAIMessageInput";
import { resetFilingDraftForReview } from "store/slices/filingDraftReview";
import { Message } from "stream-chat";
import { VendorEmailRequestInChatCard } from "./VendorEmailRequestInChatCard/VendorEmailRequestInChatCard";
import {
  setVendorEmailRequestsInChat,
  VendorEmailRequestsInChat,
} from "store/slices/vendorEmailRequestsInChat";
import { useCreateMerchantEmailChatMutation } from "store/apis/vendors";
import { resetDftDraftForReview } from "store/slices/dftFilingDraftReview";
import { DftDraftForReview } from "./DftDraftForReview";
import { KB_SEARCH_CHANNEL_CUSTOM_TYPE } from "constants/channelType";
import { QuillIcon } from "components/icons/QuillIcon";
import { RequestHumanSupport } from "./RequestHumanSupport";
import { ResolveHumanSupport } from "./ResolveHumanSupport";
import { JoinHumanSupport } from "components/MessageInputInfo/JoinHumanSupport";
import { ChatComposerEditor } from "./ChatComposer/ChatComposerEditor";
import { ChatComposerToolbar } from "./ChatComposer/ChatComposerToolbar";
import { useChatComposerEditor } from "./ChatComposer/useChatComposerEditor";

type EmojiSelection = { native: string };

export const MessageInput = <
  StreamChatGenerics extends DefaultStreamChatGenerics = DefaultStreamChatGenerics,
  V extends CustomTrigger = CustomTrigger
>(
  props: MessageInputProps<StreamChatGenerics, V> &
    ChatAutoCompleteProps & {
      isThreadMessage?: boolean;
      hideSLABanner?: boolean;
      entityId?: string;
      showAISuggestions?: boolean;
      disableAutoFocus?: boolean;
      messageSource?: string;
    }
) => {
  const { disableAutoFocus } = props;
  const { client } = useChatContext();
  const [pushToChat, { isLoading: pushingToChat }] = usePushToChatMutation();
  const [sendCardToStream, { isLoading: isSendingCardToStream }] =
    useSendCardToStreamMutation();
  const [pushVendorEmailReqToChat, { isLoading: pushingVendorEmailReqToChat }] =
    useCreateMerchantEmailChatMutation();
  const [sendClosingToChat, { isLoading: isSendClosingToChatLoading }] =
    useSendClosingToChatMutation();
  const [updateStatus, { isLoading: isStatusUpdating }] =
    useUpdateFinancialClosingStatusMutation();
  const { trackEvent } = useAnalytics();

  const { localData: hasUsedStrengthen, setItem: setHasUsedStrengthen } =
    useLocalStorage<boolean>("strengthen_message_used", false);
  const isStrengthenNew = !hasUsedStrengthen;

  const { localData: isRichTextMode, setItem: setIsRichTextMode } =
    useLocalStorage<boolean>(RICH_TEXT_MODE_STORAGE_KEY, false);

  const [templateOpen, setTemplateOpen] = useState(false);
  const [responseUsed, setResponseUsed] = useState<null | string>(null);
  const isGeneratingMessage = useSelector(
    (state: RootState) => state.generateChatMessage.generatingMessage
  );

  const { files } = useSelector((state: RootState) => state.chat);
  const isSearchActive = useSelector(
    (state: RootState) => state.chatSearch.isActive
  );
  const { showAIChannels, showOnlyAdminMessage } = useSelector(
    (state: RootState) => state.chatSearch
  );
  const {
    customAttachment: attachments,
    customLinks,
    docReviewPayload,
  } = useSelector((state: RootState) => state.reviewAndBalancePayment);

  const transactionsInChat = useAppSelector(
    (state) => state.transactionsInChat.transactionsInChat
  );

  const estimatedTaxCard = useAppSelector(
    (state) => state.estimatedTaxCardInChat
  );

  const {
    vendorEmailRequestsInChat,
    entityId: vendorEmailRequestEntityId,
    season: vendorEmailRequestSeason,
  } = useAppSelector((state) => state.vendorEmailRequestsInChat);

  const financialClosingCardAttachment = useAppSelector(
    (state) => state.financialClosing.chatCardAttachment
  );

  const { data: financialClosingDetails } = useGetFinancialClosingQuery(
    {
      entityId: financialClosingCardAttachment.entityId,
      financialClosingId: financialClosingCardAttachment.financialClosingId,
    },
    {
      skip: !financialClosingCardAttachment.financialClosingId,
    }
  );

  const { closing_card_details } = financialClosingDetails || {};

  const cartInChatEntityId = useAppSelector(
    (state) => state.cartInChat.entityId
  );

  const entityId = useAppSelector((state) => state.transactionsInChat.entityId);
  const {
    reviewData: filingDraftReview,
    deletedFields,
    editedFields,
  } = useAppSelector((state) => state.filingDraftReview);

  const dftReviewData = useAppSelector((state) => state.dftFilingDraftReview);

  const attachmentForReview =
    attachments.length !== 0 || customLinks.length !== 0;

  const id = useId();
  const dispatch = useDispatch();
  const { isForeignCA, isAdmin, isCpa, isCustomer } = useRoleBasedView();
  const isSmallScreen = useIsSmallScreen();
  // Composer keeps its 12px size without iOS zooming the page on focus
  useLockViewportScale();

  const {
    handleSubmit,
    isUploadEnabled,
    uploadNewFiles,
    maxFilesLeft,
    numberOfUploads,
  } = useMessageInputContext();

  const { channel } = useChannelStateContext();
  const channelId = channel.id || "";

  const [generateMessageResponse] = useGenerateResponseForMessageMutation();
  const [strengthenMessageMutation, { isLoading: isStrengtheningMessage }] =
    useStrengthenMessageMutation();

  const [createUpdateDraft] = useCreateMessageDraftMutation();
  const [sendDocumentToReview, { isLoading }] =
    useSendDocumentToReviewMutation();
  const { alertToast, successToast } = useToast();
  const { data: channelTask } = useGetTaskFromChannelIdQuery(
    {
      channelId: channelId,
    },
    {
      skip: !channelId,
    }
  );

  const { tasks, company_group: companyGroup, entity } = channelTask || {};
  const task =
    tasks?.find((task) => task.is_parent_task) || tasks?.[1] || tasks?.[0];
  const { uuid: groupUuid } = useCurrentChatGroupContext();
  const isV2Platform = useIsGroupV2Platform();
  const { taskId: taskUuid } = useParams<{ taskId: string }>();
  const [sendCartToChat, { isLoading: isSendingCartToChat }] =
    useSendCartToChatMutation();
  const [createFilingDraft, { isLoading: isCreatingFilingDraft }] =
    useCreateFilingDraftMutation();
  const [createDftDraft, { isLoading: isCreatingDftDraft }] =
    useCreateDftDraftMutation();

  const { data: { message_draft } = { message_draft: "" } } =
    useGetDraftMessageQuery(
      { channelUrl: channelId },
      {
        refetchOnMountOrArgChange: true,
        skip: !channelId,
      }
    );

  const { messageId } = useSelector((state: RootState) => state.messageToQuote);
  const messageToQuote = useGetMessage(messageId!);

  const messageInput = useMessageInputContext<StreamChatGenerics, V>(
    "ChatAutoComplete"
  );
  const { cooldownRemaining, disabled } = messageInput;

  const isStrengthenMessageDisabled =
    isStrengtheningMessage ||
    messageInput.text.trim().split(/\s+/).filter(Boolean).length < 20;

  const placeholder = cooldownRemaining ? "Slow Mode ON" : "Type your message";

  const onInput = useCallback(
    (message: string) => {
      createUpdateDraft({
        channelUrl: channelId,
        message,
      });
    },
    [channelId]
  );

  const debouncedOnInput = useCallback(debounce(onInput, 300), [onInput]);

  const isMessageSending =
    pushingVendorEmailReqToChat ||
    pushingToChat ||
    isLoading ||
    isSendClosingToChatLoading ||
    isStatusUpdating ||
    isSendingCartToChat ||
    isCreatingFilingDraft ||
    isCreatingDftDraft ||
    isSendingCardToStream;

  const handleComposerTextChange = (markdown: string) => {
    messageInput.setText(markdown);
    debouncedOnInput(markdown);

    if (markdown) {
      channel
        .keystroke(props.parent?.id)
        .catch((error) => console.error("Failed to send typing event", error));
    }
  };

  const composer = useChatComposerEditor({
    text: messageInput.text,
    placeholder,
    isDisabled:
      disabled ||
      !!cooldownRemaining ||
      isMessageSending ||
      isGeneratingMessage,
    isMentionDisabled: Boolean(messageInput.disableMentions),
    onTextChange: handleComposerTextChange,
    onSubmit: () => handleOwnSubmit(undefined, undefined),
    onSelectUser: messageInput.onSelectUser,
    onPasteFiles: isUploadEnabled ? uploadNewFiles : undefined,
  });
  const { editor } = composer;

  useEffect(() => {
    if (filingDraftReview) {
      messageInput.setText(
        "Hey, the draft for your 1120 is now ready. Please go through it as per your convenience. Once you approve it, we’ll proceed with the final submission."
      );
    }
  }, [filingDraftReview]);

  useEffect(() => {
    if (Boolean(dftReviewData)) {
      messageInput.setText(
        `We have shared the draft of the Delaware State Franchise Tax filing above for your review. **The total amount due for Delaware franchise tax is $${dftReviewData?.taxAmount!}**.\n\nPlease note that after submitting this filing, the Delaware Department will take 5-10 business days to withdraw the payment from your bank account. Make sure your account has sufficient funds and that you have authorized an ACH pull through your online banking platform.\nThe Delaware Secretary of State will charge an additional $50 if the payment fails.\n\nPlease let us know if you have any questions on this.\n\nThanks!`
      );
    }
  }, [Boolean(dftReviewData), dftReviewData?.taxAmount]);

  useEffect(() => {
    if (files) {
      uploadNewFiles(files);
      dispatch(setDropedFile(null));
    }
  }, [files]);

  useEffect(() => {
    if (transactionsInChat.length > 0) {
      editor.commands.focus("end");
    }
  }, [transactionsInChat.length, editor]);

  useEffect(() => {
    if (estimatedTaxCard) {
      editor.commands.focus("end");
    }
  }, [estimatedTaxCard, editor]);

  useEffect(() => {
    if (companyGroup?.uuid) {
      dispatch(setActiveChannelGroupId(companyGroup?.uuid));
    }
  }, [companyGroup?.uuid]);

  useEffect(() => {
    messageInput.setText(message_draft || "");
  }, [message_draft]);

  useEffect(() => {
    // Opening a channel should not pop the keyboard open on mobile
    if (!isSearchActive && !disableAutoFocus && !isSmallScreen) {
      editor.commands.focus("end");
    }
  }, [channel.id, isSearchActive, isSmallScreen, editor]);

  const {
    acceptedFiles = [],
    multipleUploads,
    quotedMessage,
  } = useChannelStateContext<StreamChatGenerics>("MessageInputV2");

  const { data: aiResponse } = useGetLatestAIGeneratedMessageQuery(
    {
      channelId: channelId,
    },
    {
      skip: !channelId || !isAdmin,
    }
  );

  const [updateGeneratedMessage, { isLoading: updatingGeneratedMessage }] =
    useUpdateAIGeneratedMessageMutation();

  const onArchiveAIMessage = async () => {
    if (aiResponse?.message) {
      try {
        await updateGeneratedMessage({
          channelId: channelId,
          responseId: aiResponse.uuid,
          archived: true,
        }).unwrap();
      } catch (error: any) {
        alertToast(
          { message: (error as BackendError).data?.error?.message },
          error
        );
      }
    }
  };

  const generateResponse = async () => {
    const lastmessage = channel.state.messages
      .filter((message) => {
        return (
          (message.user?.role_name === "" || !message.user?.role_name) &&
          message.text &&
          message.type !== "system"
        );
      })
      .at(-1);

    if (lastmessage?.text) {
      try {
        dispatch(
          setGeneratingMessage({
            generating: true,
            channelId: channelId,
          })
        );
        await generateMessageResponse({
          message: lastmessage.text,
          channelId: channelId,
        }).unwrap();
        dispatch(resetGeneratingMessage());
      } catch (error: any) {
        alertToast(
          { message: (error as BackendError).data?.error?.message },
          error
        );
      }
    }
  };

  const strengthenMessage = async () => {
    if (isStrengthenNew) {
      setHasUsedStrengthen(true);
    }
    try {
      const result = await strengthenMessageMutation({
        message: messageInput.text,
      }).unwrap();
      editor
        .chain()
        .focus()
        .setContent(result.strengthened_message, { contentType: "markdown" })
        .run();
    } catch (error: any) {
      alertToast(
        { message: (error as BackendError).data?.error?.message },
        error
      );
    }
  };

  const { setQuotedMessage } =
    useChannelActionContext<StreamChatGenerics>("MessageActionsBox");
  useEffect(() => {
    if (messageId && messageToQuote) {
      const formattedMessage = {
        ...messageToQuote,
        created_at: new Date(messageToQuote.created_at || ""),
        pinned_at: messageToQuote.pinned_at
          ? new Date(messageToQuote.pinned_at)
          : null,
        updated_at: new Date(messageToQuote.updated_at || ""),
      };
      setQuotedMessage(formattedMessage as StreamMessage<StreamChatGenerics>);
    }
  }, [messageId, messageToQuote]);

  const isAIChannel = channel?.data?.custom_type === AI_CHANNEL;
  const isKBChannelEarly =
    channel?.data?.custom_type === KB_SEARCH_CHANNEL_CUSTOM_TYPE;
  const threadParentId = props.parent?.id;

  const [isAIThinking, setIsAIThinking] = useState(false);
  const thinkingMessageIdRef = useRef<string | null>(null);

  useEffect(() => {
    const isActive = (isAIChannel && threadParentId) || isKBChannelEarly;
    if (!isActive) return;

    const handleEvent = (event: any) => {
      const msg = event.message;
      if (!msg) return;

      if (isAIChannel && msg.parent_id !== threadParentId) return;

      const customData = msg.custom_data as Record<string, any>;
      const thinking = !!customData?.ai_thinking;
      setIsAIThinking(thinking);
      thinkingMessageIdRef.current = thinking ? msg.id : null;
    };

    channel.on("message.new", handleEvent);
    channel.on("message.updated", handleEvent);

    return () => {
      channel.off("message.new", handleEvent);
      channel.off("message.updated", handleEvent);
    };
  }, [channel, isAIChannel, threadParentId, isKBChannelEarly]);

  if (channel.data?.frozen) {
    return <ChannelFrozenBlocker />;
  }

  const removeDraftMessage = () => {
    createUpdateDraft({
      channelUrl: channelId,
      message: null,
    });
  };

  const sendReviewDocumentMessage = async () => {
    try {
      const inkleDocs: string[] = [];
      const localFiles: Blob[] = [];
      attachments.forEach((attachment) => {
        if ("uuid" in attachment) {
          inkleDocs.push(attachment.uuid);
        } else {
          //@ts-ignore
          localFiles.push(attachment);
        }
      });
      const groupId = companyGroup?.uuid || groupUuid;
      const taskId = task?.uuid || taskUuid;
      const formData = new FormData();
      formData.append("file_ids", inkleDocs.join(","));
      formData.append("message", messageInput.text);
      localFiles.forEach((file) => formData.append("files", file));
      formData.append("task_id", taskId);
      formData.append("group_id", groupId);
      const mentionedUserIds = messageInput.mentioned_users?.map((u) => u.id);
      if (mentionedUserIds?.length) {
        formData.append(
          "mentioned_stream_user_ids",
          mentionedUserIds.join(",")
        );
      }
      if (entity?.uuid) {
        formData.append("entity_id", entity?.uuid);
      }
      formData.append("custom_type", CHAT_CARD_TYPE);

      if (docReviewPayload.document_operation === TASK_REVIEW_DOCUMENT) {
        formData.append("document_operation", TASK_REVIEW_DOCUMENT);
      }

      if (docReviewPayload.document_operation === TASK_REVIEW_AND_PAY) {
        formData.append("document_operation", TASK_REVIEW_AND_PAY);
        formData.append("price", docReviewPayload?.amount!);
        formData.append("description", docReviewPayload?.description!);
        //@ts-ignore
        formData.append("payment_required", true);
      }

      if (customLinks.length > 0) {
        formData.append("links", JSON.stringify(customLinks));
      }

      await sendDocumentToReview({
        channel_id: channelId,
        payload: formData,
      }).unwrap();
      dispatch(clearCustomAttachment());
      dispatch(clearCustomLinks());
      messageInput.setText("");
      removeDraftMessage();
    } catch (e: any) {
      alertToast({ message: e?.data?.error?.message });
    }
  };

  const sendVendorEmailRequestsToChat = async () => {
    const merchantIds = vendorEmailRequestsInChat
      .map((merchant: VendorEmailRequestsInChat) => merchant.id)
      .join(",");

    if (channel.id) {
      try {
        const payload = {
          groupId: groupUuid,
          channel_url: channel.id,
          merchant_ids: merchantIds,
          message: messageInput.text,
          task_id: taskUuid,
          entity_id: vendorEmailRequestEntityId,
          season: vendorEmailRequestSeason,
        };

        const res = await pushVendorEmailReqToChat(payload).unwrap();
        const messageId = res.message_id;
        dispatch(setScrollToMessageId(messageId));
        dispatch(
          setVendorEmailRequestsInChat({
            vendorEmailRequestsInChat: [],
            entityId: "",
            season: "",
          })
        );
        messageInput.setText("");
        removeDraftMessage();
      } catch (error) {
        alertToast({ message: "Failed to send to chat" });
      }
    }
  };

  const sendEstimatedTaxToChat = async () => {
    if (!estimatedTaxCard) return;
    try {
      await sendCardToStream({
        entityId: estimatedTaxCard.entityId,
        quarter: estimatedTaxCard.quarter,
        year: estimatedTaxCard.year,
        message_text: messageInput.text,
      }).unwrap();
      dispatch(setToOpenChatId(estimatedTaxCard.channel_url));
      dispatch(clearEstimatedTaxCardInChat());
      messageInput.setText("");
      removeDraftMessage();
    } catch {
      alertToast({ message: "Failed to send to chat" });
    }
  };

  const sendTransactionsToChat = async () => {
    if (channel.id) {
      try {
        const payload = {
          channel_url: channel.id,
          custom_type: TRANSACTIONS,
          transaction_ids: transactionsInChat.map((txn) => txn.id).join(","),
          message: messageInput.text,
        };
        const { message_id } = await pushToChat({
          groupId: groupUuid,
          payload,
          entityId,
        }).unwrap();
        // successToast({ message: "Transaction sent to chat" });
        dispatch(setScrollToMessageId(message_id));
        dispatch(
          setTransactionsInChat({ transactionsInChat: [], entityId: "" })
        );
        messageInput.setText("");
      } catch (error) {
        alertToast({ message: "Failed to send to chat" });
      }
    }
  };

  const {
    card_type,
    entityId: attachmentEntityId,
    financialClosingId,
    status,
  } = financialClosingCardAttachment;

  const sendFinancialClosingToChat = async () => {
    if (channel.id && card_type) {
      try {
        if (card_type === FINANCIAL_CLOSING_CARD_TYPE.REVIEW_FINANCIAL) {
          await updateStatus({
            entityId: attachmentEntityId,
            financialClosingId,
            payload: {
              status: status,
              accounting_lock_date: closing_card_details?.accounting_lock_date,
            },
          }).unwrap();

          const { message_id } = await sendClosingToChat({
            entityId: attachmentEntityId,
            financialClosingId,
            card_type,
            channel_url: channelId,
            message: messageInput.text,
            mentioned_stream_user_ids: messageInput.mentioned_users?.map(
              (u) => u.id
            ),
          }).unwrap();
          dispatch(setScrollToMessageId(message_id));
        }

        dispatch(resetChatCardAttachment());
        messageInput.setText("");
        removeDraftMessage();
      } catch (error) {
        alertToast(
          { message: (error as BackendError).data?.error?.message },
          error as Error
        );
      }
    }
  };

  const sendCartToChatCall = async () => {
    try {
      await sendCartToChat({
        groupId: (channel?.data?.group_uuid as string) || groupUuid,
        entityId: cartInChatEntityId,
        channelId,
        message: messageInput.text,
      }).unwrap();
      trackEvent(ATTACH_CART);
      messageInput.setText("");
      dispatch(setCartInChat({ entityId: "" }));
    } catch (error) {
      alertToast(
        { message: (error as BackendError).data?.error?.message },
        error as Error
      );
    }
  };

  const handleCreateFilingDraft = async () => {
    try {
      if (filingDraftReview) {
        createFilingDraft({
          groupId: (channel?.data?.group_uuid as string) || groupUuid,
          channel_id: channelId,
          request_data: filingDraftReview,
          message: messageInput.text,
          deletedFields,
          editedFields,
        }).unwrap();
        messageInput.setText("");
      }
    } catch (error) {
      alertToast(
        { message: (error as BackendError).data?.error?.message },
        error as Error
      );
    } finally {
      dispatch(resetFilingDraftForReview());
    }
  };

  const handleCreateDftDraft = async () => {
    try {
      if (dftReviewData) {
        createDftDraft({
          groupId: (channel?.data?.group_uuid as string) || groupUuid,
          channelId: channelId,
          requestData: {
            file_id: dftReviewData.file.uuid,
            tax_amount_payable: dftReviewData.taxAmount,
            gross_asset_value: dftReviewData.grossAssetValue,
          },
          message: messageInput.text,
        }).unwrap();
        messageInput.setText("");
        successToast({ message: "Draft sent for review" });
      }
    } catch (error) {
      alertToast(
        { message: (error as BackendError).data?.error?.message },
        error as Error
      );
    } finally {
      dispatch(resetDftDraftForReview());
    }
  };

  const handleOwnSubmit = async (e: any, customMessage: any) => {
    if (isAIThinking) return;
    if (filingDraftReview) {
      handleCreateFilingDraft();
    } else if (dftReviewData) {
      handleCreateDftDraft();
    } else if (estimatedTaxCard) {
      sendEstimatedTaxToChat();
    } else if (transactionsInChat.length > 0) {
      sendTransactionsToChat();
    } else if (attachmentForReview) {
      sendReviewDocumentMessage();
    } else if (financialClosingCardAttachment.financialClosingId) {
      sendFinancialClosingToChat();
    } else if (cartInChatEntityId.length > 0) {
      sendCartToChatCall();
    } else if (props.entityId) {
      messageInput.handleSubmit(e, {
        text: messageInput.text,
        custom_data: {
          entity_id: props.entityId,
          source: props?.messageSource,
        },
      } as Partial<Message>);
    } else if (vendorEmailRequestsInChat.length > 0) {
      sendVendorEmailRequestsToChat();
    } else if (props.messageSource) {
      messageInput.handleSubmit(e, {
        text: messageInput.text,
        custom_data: {
          source: props.messageSource,
        },
      } as Partial<Message>);
    } else {
      props.handleSubmit?.(e) || messageInput.handleSubmit(e, customMessage);
    }
    removeDraftMessage();

    if (aiResponse?.uuid) {
      updateGeneratedMessage({
        channelId: channelId,
        responseId: aiResponse?.uuid,
        messageSent: messageInput.text,
      });
    }
  };

  const handleSubmitFromButton = (e: any) => {
    if (isAIThinking) return;
    if (filingDraftReview) {
      handleCreateFilingDraft();
    } else if (dftReviewData) {
      handleCreateDftDraft();
    } else if (estimatedTaxCard) {
      sendEstimatedTaxToChat();
    } else if (transactionsInChat.length > 0) {
      sendTransactionsToChat();
    } else if (attachmentForReview) {
      sendReviewDocumentMessage();
    } else if (financialClosingCardAttachment.financialClosingId) {
      sendFinancialClosingToChat();
    } else if (cartInChatEntityId.length > 0) {
      sendCartToChatCall();
    } else if (props.entityId) {
      messageInput.handleSubmit(e, {
        text: messageInput.text,
        custom_data: {
          entity_id: props.entityId,
          source: props?.messageSource,
        },
      } as Partial<Message>);
    } else if (vendorEmailRequestsInChat.length > 0) {
      sendVendorEmailRequestsToChat();
    } else if (props.messageSource) {
      messageInput.handleSubmit(e, {
        text: messageInput.text,
        custom_data: {
          source: props.messageSource,
        },
      } as Partial<Message>);
    } else {
      handleSubmit(e);
    }
    removeDraftMessage();

    if (aiResponse?.uuid) {
      updateGeneratedMessage({
        channelId: channelId,
        responseId: aiResponse?.uuid,
        messageSent: messageInput.text,
      });
    }
  };

  const replaceComposerText = (text: string) => {
    messageInput.setText(text);
    debouncedOnInput(text);
    editor.commands.focus("end");
  };

  const selectTemplate = (message: string) =>
    replaceComposerText(`${messageInput.text}${message}`);

  const insertAIResponse = () => {
    if (!aiResponse) return;

    setResponseUsed(aiResponse.uuid);
    replaceComposerText(messageInput.text + aiResponse.message);
  };

  const insertEmoji = (emoji: EmojiSelection) =>
    editor.chain().focus().insertContent(emoji.native).run();

  // Clicking the composer's padding focuses the editor, like a textarea would
  const focusComposer = (event: React.MouseEvent<HTMLDivElement>) => {
    const { target } = event;
    const isHandledByTarget =
      target instanceof HTMLInputElement ||
      (target instanceof Node && editor.view.dom.contains(target));
    if (isHandledByTarget) return;

    editor.commands.focus("end");
  };

  const openRichText = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setIsRichTextMode(!isRichTextMode);
  };

  const contentPresentInInput =
    messageInput.text.length > 0 ||
    messageInput.fileOrder.length > 0 ||
    messageInput.imageOrder.length > 0 ||
    cartInChatEntityId.length > 0 ||
    vendorEmailRequestsInChat?.length > 0;

  const isInkleAIChannel = channel?.data?.custom_type === AI_CHANNEL;
  const isKBChannel =
    channel?.data?.custom_type === KB_SEARCH_CHANNEL_CUSTOM_TYPE;
  const showAiMessageBox =
    aiResponse?.message &&
    aiResponse.uuid !== responseUsed &&
    !aiResponse.is_archived &&
    !isGeneratingMessage;

  return (
    <div className="t-relative t-w-full safe-area-bottom">
      {(props.showAISuggestions || (showAIChannels && showOnlyAdminMessage)) &&
        !isKBChannel && (
          <div className="t-px-4 t-pb-2">
            <InkleAISuggestions label="Get started:" />
          </div>
        )}
      {isAdmin && <JoinHumanSupport />}
      {!isKBChannel && (
        <MessageInputInfo
          isThreadMessage={props.isThreadMessage}
          hideSLABanner={props.hideSLABanner}
        />
      )}
      {isRichTextMode && !isKBChannel && (
        <div
          className={classNames(
            "t-bg-surface-lighter-grey t-rounded-t t-p-1.5",
            {
              "t-mx-2": !props.isThreadMessage,
            }
          )}
        >
          <ChatComposerToolbar
            editor={editor}
            onOpenLinkInput={composer.openLinkInput}
          />
        </div>
      )}
      <div
        className={classNames("t-relative t-cursor-text t-bg-white t-p-2", {
          "t-mx-2 t-mb-2": !props.isThreadMessage,
          "t-rounded": !isRichTextMode,
          "t-rounded-b": isRichTextMode,
        })}
        onClick={focusComposer}
      >
        {isLoading ? (
          <span className="t-flex t-items-center">
            <span className="t-flex t-animate-spin">
              <LoadingIcon />
            </span>
          </span>
        ) : (
          <div className="t-flex t-flex-col t-relative t-gap-3 ">
            {quotedMessage && (
              <>
                <button
                  className="t-flex t-flex-col t-gap-2 t-rounded t-bg-surface-lighter-grey t-p-3 t-max-h-[40vh] t-overflow-auto all:unset"
                  onClick={() => {
                    dispatch(setScrollToMessageId(quotedMessage.id));
                  }}
                >
                  <div
                    className={classNames(
                      "t-rounded-lg t-border-l-4 t-border-solid t-border-0",
                      {
                        "t-border-neutral-40":
                          quotedMessage.user?.id !== client.userID,
                        "t-border-purple-30":
                          quotedMessage.user?.id === client.userID,
                      }
                    )}
                  >
                    <MessageBody
                      // @ts-ignore
                      message={{
                        ...(quotedMessage.forwarded_message || quotedMessage),
                        quoted_message: undefined,
                      }}
                      otherData={{ sentByTheUser: false }}
                    />
                  </div>
                </button>
                <div className="t-absolute t-right-0 t-top-0 t-z-10">
                  <Button
                    customType="ghost_icon"
                    onClick={() => {
                      setQuotedMessage(undefined);
                      dispatch(removeMessageToQuote());
                    }}
                  >
                    <span className="t-fill-surface-lighter-grey">
                      <Cross />
                    </span>
                  </Button>
                </div>
              </>
            )}
            {estimatedTaxCard && (
              <EstimatedTaxCardInChat card={estimatedTaxCard} />
            )}
            {transactionsInChat.length > 0 && (
              <TransactionsInChatCard transactionsInChat={transactionsInChat} />
            )}
            {vendorEmailRequestsInChat?.length > 0 && (
              <VendorEmailRequestInChatCard
                vendors={vendorEmailRequestsInChat}
              />
            )}
            {cartInChatEntityId.length > 0 && (
              <CartAttachmentPreviews
                entityId={cartInChatEntityId}
                groupId={(channel?.data?.group_uuid as string) || groupUuid}
              />
            )}
            {financialClosingCardAttachment.financialClosingId && (
              <FinancialClosingCardAttachment
                attachment={financialClosingCardAttachment}
              />
            )}

            {isUploadEnabled && !!numberOfUploads && <AttachmentPreviewList />}
            {attachmentForReview && <CustomAttachmentPreviewList />}
            {filingDraftReview && <FilingDraftForReview />}
            {dftReviewData && <DftDraftForReview />}

            <motion.div className="t-flex t-flex-col ">
              <ChatComposerEditor composer={composer} />

              {isGeneratingMessage && (
                <motion.div className="t-flex t-items-center t-gap-1.5">
                  <Sparkle />
                  <span className="t-bg-inkle-gradient t-bg-clip-text t-text-surface-transparent t-text-subtext-sm">
                    Generating...
                  </span>
                </motion.div>
              )}

              {showAiMessageBox && (
                <motion.div className="t-relative t-rounded-lg t-p-3 t-mb-2 t-border t-border-neutral-10 t-bg-white t-border-solid">
                  <div className="t-flex t-items-center t-justify-between t-mb-2">
                    <div className="t-flex t-items-center t-gap-2">
                      <span className="t-flex t-items-center">
                        <AISparkle />
                      </span>
                      <span className="t-bg-inkle-gradient t-bg-clip-text t-text-surface-transparent t-text-subtext-sm">
                        AI Generated Response
                      </span>
                    </div>
                    <div className="t-flex t-items-center t-gap-2">
                      <Button
                        customType="primary_outlined"
                        size="small"
                        onClick={insertAIResponse}
                      >
                        Insert
                      </Button>
                      {isAdmin && (
                        <Button
                          isLoading={updatingGeneratedMessage}
                          disabled={updatingGeneratedMessage}
                          customType="ghost_icon"
                          size="small"
                          onClick={onArchiveAIMessage}
                        >
                          <Cross />
                        </Button>
                      )}
                    </div>
                  </div>
                  <p className="t-text-text-30 t-text-body-sm t-m-0">
                    {aiResponse.message}
                  </p>
                </motion.div>
              )}

              {!isGeneratingMessage && !showAIChannels && (
                <motion.div className="t-flex t-w-full t-items-end">
                  {!isKBChannel && (
                    <div className="t-flex t-items-center t-gap-0">
                      <UploadButton
                        accept={acceptedFiles?.join(",")}
                        aria-label="File upload"
                        className="t-hidden"
                        data-testid="file-input"
                        disabled={!isUploadEnabled || maxFilesLeft === 0}
                        id={id}
                        multiple={multipleUploads}
                        onFileChange={uploadNewFiles}
                      />

                      {isAdmin && !isForeignCA && (
                        <ToolTip text="Generate Response">
                          <Button
                            customType="ghost_icon"
                            size="xsmall"
                            onClick={generateResponse}
                          >
                            <Sparkle />
                          </Button>
                        </ToolTip>
                      )}

                      {(isCpa || isAdmin) && !isForeignCA && (
                        <ToolTip text="Strengthen Message">
                          <div className="t-relative t-inline-flex">
                            <Button
                              customType="ghost_icon"
                              size="xsmall"
                              isLoading={isStrengtheningMessage}
                              disabled={isStrengthenMessageDisabled}
                              onClick={strengthenMessage}
                            >
                              <QuillIcon
                                disabled={isStrengthenMessageDisabled}
                              />
                            </Button>
                            {isStrengthenNew &&
                              !isStrengthenMessageDisabled && (
                                <span className="t-animate-pulse t-absolute -t-top-0.5 -t-right-0.5 t-size-2 t-rounded-full t-bg-red-50 t-z-10" />
                              )}
                          </div>
                        </ToolTip>
                      )}

                      {(isCpa || isAdmin) && !isForeignCA && (
                        <Button customType="ghost_icon" size="xsmall">
                          <ChatTemplate
                            selectTemplate={selectTemplate}
                            dropdownOpen={templateOpen}
                            setDropdownOpen={setTemplateOpen}
                          />
                        </Button>
                      )}
                      <Button
                        customType="ghost_icon"
                        size="xsmall"
                        disabled={
                          attachmentForReview || transactionsInChat.length > 0
                        }
                        onClick={() => {
                          if (
                            !attachmentForReview &&
                            transactionsInChat.length === 0
                          ) {
                            document.getElementById(id)?.click();
                          }
                        }}
                      >
                        <div className="t-text-neutral-50">
                          <Paperclip />
                        </div>
                      </Button>

                      {!isInkleAIChannel && (
                        <div
                          className="str-chat__emojiselect-wrapper"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <EmojiPicker
                            popperOptions={{ placement: "top-start" }}
                            pickerProps={{
                              theme: "light",
                              onEmojiSelect: insertEmoji,
                            }}
                            buttonClassName="all:unset"
                            ButtonIconComponent={() => (
                              <Button customType="ghost_icon" size="xsmall">
                                <div className="t-text-neutral-50">
                                  <Smiley />
                                </div>
                              </Button>
                            )}
                          />
                        </div>
                      )}
                      {isAdmin && <AdminCall />}
                      <ToolTip
                        text={
                          isRichTextMode ? "Hide formatting" : "Show formatting"
                        }
                      >
                        <Button
                          customType="ghost_icon"
                          size="xsmall"
                          onClick={openRichText}
                        >
                          <span
                            className={classNames("t-text-xs t-font-semibold", {
                              "t-text-purple t-underline": isRichTextMode,
                              "t-text-neutral-50": !isRichTextMode,
                            })}
                          >
                            Aa
                          </span>
                        </Button>
                      </ToolTip>
                      {(isCpa || isAdmin || isForeignCA) && (
                        <Button customType="ghost_icon" size="xsmall">
                          <span onClick={(e) => e.stopPropagation()}>
                            <TaskActionsFromChat />
                          </span>
                        </Button>
                      )}
                    </div>
                  )}

                  <div className="t-ml-auto t-flex t-items-center t-gap-2">
                    {isCustomer && isV2Platform && (
                      <RequestHumanSupport groupId={groupUuid} />
                    )}
                    {isAdmin && <ResolveHumanSupport />}
                    <Button
                      customType="ghost_icon"
                      disabled={
                        isAIThinking ||
                        !contentPresentInInput ||
                        isMessageSending
                      }
                      onClick={handleSubmitFromButton}
                      size="small"
                      isLoading={isMessageSending}
                    >
                      <span
                        className={cx("t-transition-all", {
                          "t-text-purple":
                            contentPresentInInput && !isAIThinking,
                          "t-text-neutral-40":
                            !contentPresentInInput || isAIThinking,
                        })}
                      >
                        <PaperPlaneRight />
                      </span>
                    </Button>
                  </div>
                </motion.div>
              )}
            </motion.div>
          </div>
        )}
      </div>
    </div>
  );
};
